/* WebNest — shared NS* form/modal helpers (ported from the redesign prototype).
   These wrap the design-system CSS classes/vars already defined in index.css so
   ported screens render 1:1 with the prototype. */
import React, { useState } from 'react'
import { I, Icon } from './ns-icons'

/* ---------------- modal shell ---------------- */
export function NSModal({ icon, tone, title, subtitle, onClose, width = 620, foot, children }) {
  return (
    <div className="drawer-backdrop" style={{ justifyContent: 'center', alignItems: 'center', animation: 'fadeIn 0.16s ease' }} onClick={onClose}>
      <div className="card" style={{ width, maxWidth: '94vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)', animation: 'slideDown 0.22s cubic-bezier(0.22,1,0.36,1)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {icon && <span className={`icon-tile ${tone || ''}`} style={{ width: 36, height: 36, flexShrink: 0 }}><Icon name={icon} /></span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h2>
            {subtitle && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className="site-action" onClick={onClose}><I.x /></button>
        </div>
        <div style={{ padding: 22, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {children}
        </div>
        {foot && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {foot}
          </div>
        )}
      </div>
    </div>
  )
}

/* small reusable chip-toggle for multi-select access lists */
export function NSChip({ label, on, onClick, icon }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 11px', borderRadius: 999,
      fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.13s ease',
      border: `1px solid ${on ? 'var(--border-accent)' : 'var(--border)'}`,
      background: on ? 'var(--accent-soft)' : 'var(--surface)',
      color: on ? 'var(--accent-3)' : 'var(--text-soft)',
    }}>
      {on ? <I.check style={{ fontSize: 13 }} /> : icon ? <Icon name={icon} style={{ fontSize: 13 }} /> : null}
      {label}
    </button>
  )
}

/* ---------------- form fields ---------------- */
export function NSToggle({ on, onChange }) {
  const [state, setState] = useState(!!on)
  const v = onChange ? on : state
  const flip = (e) => { e.stopPropagation(); onChange ? onChange(!on) : setState(!state) }
  return (
    <button type="button" onClick={flip} style={{
      width: 34, height: 19, borderRadius: 999, flexShrink: 0,
      background: v ? 'var(--accent)' : 'var(--surface-2)',
      position: 'relative', transition: 'background 0.15s ease',
      border: '1px solid var(--border)',
    }}>
      <span style={{
        position: 'absolute', top: 1, left: v ? 16 : 1,
        width: 15, height: 15, borderRadius: '50%', background: 'white',
        transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }} />
    </button>
  )
}

export function NSField({ label, hint, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

export function NSPrefix({ prefix, value, defaultValue, placeholder, onChange }) {
  return (
    <div style={{ display: 'flex' }}>
      <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{prefix}</span>
      <input className="input" value={value} defaultValue={defaultValue} placeholder={placeholder} onChange={onChange} style={{ borderRadius: '0 10px 10px 0' }} />
    </div>
  )
}

export function NSSelect({ value, defaultValue, options, onChange }) {
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }))
  return (
    <select className="input" value={value} defaultValue={defaultValue} onChange={onChange} style={{ appearance: 'none', backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%237B8198' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 34 }}>
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

export function NSSegment({ options, value, onChange }) {
  return (
    <div className="segmented" style={{ width: '100%' }}>
      {options.map((o) => (
        <button type="button" key={o} className={value === o ? 'on' : ''} style={{ flex: 1, justifyContent: 'center', padding: '8px 9px' }} onClick={() => onChange(o)}>{o}</button>
      ))}
    </div>
  )
}

export function NSDrop({ icon = 'upload', title, hint, height = 130, accent, onClick, onFiles }) {
  const inputRef = React.useRef(null)
  const handleClick = () => { if (onFiles) inputRef.current?.click(); if (onClick) onClick() }
  return (
    <div onClick={handleClick} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (onFiles) onFiles(Array.from(e.dataTransfer.files)) }} style={{
      height, borderRadius: 12, border: '1.5px dashed var(--border-strong)',
      background: accent ? 'var(--accent-soft)' : 'var(--surface)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 9, textAlign: 'center', padding: 16, cursor: 'pointer', transition: 'all 0.15s ease',
    }}>
      {onFiles && <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => { onFiles(Array.from(e.target.files)); e.target.value = '' }} />}
      <span className="icon-tile" style={{ width: 38, height: 38, fontSize: 17 }}><Icon name={icon} /></span>
      <div style={{ fontSize: 12.5, color: 'var(--text-soft)', fontWeight: 500 }}>{title}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
    </div>
  )
}
